"use client";

/**
 * Top-level analytics provider. Three responsibilities:
 *
 * 1. Initialize PostHog on mount if consent is already granted (returning
 *    user with a prior `wtm_consent` cookie set to analytics=true).
 *
 * 2. Listen for the `wtm:consent-changed` window event from the cookie
 *    banner and initialize PostHog immediately when the user grants
 *    consent post-mount.
 *
 * 3. Capture App Router pageviews — `posthog-js` cannot auto-capture
 *    these reliably because Next.js mocks the History API at the
 *    router layer. We hook `usePathname` + `useSearchParams` to fire
 *    `$pageview` on every route change.
 *
 * Auth-driven identification (login + subscription change) is wired
 * into a sibling effect that consumes `useSession()` from NextAuth.
 */

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import {
  enterAnonymousMode,
  initPostHog,
  posthogClient,
  identifyUser,
  resetPostHog,
  setUserProperties,
  trackEvent,
} from "@/lib/posthog";
import { hasAnalyticsConsent } from "@/lib/consent";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // Try to initialize on mount — handles returning users with prior consent.
  // `initPostHog` is async (posthog-js is dynamically imported behind the
  // consent gate to keep it out of the first-load bundle) — fire-and-forget
  // here; event wrappers no-op safely until the SDK is live.
  useEffect(() => {
    if (hasAnalyticsConsent()) {
      void initPostHog();
    }
    // Listen for runtime consent grants (first-visit users who click
    // Accept on the banner).
    function handleConsentChange(e: Event) {
      const detail = (e as CustomEvent).detail as
        | { analytics: boolean }
        | undefined;
      if (detail?.analytics) {
        void initPostHog().then((live) => {
          // Fire the initial pageview once the lazily-loaded SDK is
          // actually live so the session funnel starts at the right
          // point — capturing before the chunk resolves would drop it.
          if (!live) return;
          const client = posthogClient();
          client?.capture("$pageview");
        });
      }
    }
    window.addEventListener("wtm:consent-changed", handleConsentChange);
    return () => {
      window.removeEventListener("wtm:consent-changed", handleConsentChange);
    };
  }, []);

  return (
    <>
      <PageViewTracker />
      <AuthIdentifier />
      {children}
    </>
  );
}

/**
 * Fires `$pageview` on every Next.js App Router navigation. Lives in
 * its own component so its hook subscriptions are scoped and don't
 * remount the rest of the provider when the route changes.
 */
function PageViewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastPath = useRef<string>("");

  useEffect(() => {
    const client = posthogClient();
    if (!client) return;
    const qs = searchParams?.toString() ?? "";
    const url = qs ? `${pathname}?${qs}` : pathname;
    if (lastPath.current === url) return;
    lastPath.current = url;
    client.capture("$pageview", {
      $current_url:
        typeof window !== "undefined" ? window.location.href : url,
      pathname,
    });
  }, [pathname, searchParams]);

  return null;
}

/**
 * Mirrors NextAuth session state into PostHog identification. Fires
 * `identifyUser` on login, `resetPostHog` on logout. Tier metadata
 * (reports plan + compass tier) is attached as person properties so
 * the funnel can be sliced by plan in the PostHog dashboard.
 *
 * RM-W1-10 (ruling OR-04, variant A). The identifier is `session.user.analyticsId`,
 * an HMAC-SHA256 of the user id computed on the SERVER in the NextAuth session
 * callback. This component never imports the helper and never sees the key; it
 * receives a finished digest. The email is no longer read here at all.
 *
 * THE THREE STATES:
 *   signed in, id present  -> identify with the digest. The library leaves a
 *                             DIFFERENT persisted identity behind first.
 *   signed in, id absent   -> anonymous mode: no identification, and whatever
 *                             identity an earlier visit persisted in this
 *                             browser is left behind before the first event.
 *                             This is the unprovisioned-key state. It is not a
 *                             logout (no `resetPostHog`), and it is not "leave
 *                             the old identity alone" either, because the old
 *                             identity may be the very email RM-W1-10 exists
 *                             to stop sending (Astra H3-01).
 *   signed out             -> reset, once, if this component had identified
 *                             (the unchanged logout), then anonymous mode.
 *
 * `lastIdentified` records the identity THIS component currently has IN FORCE,
 * not every identity it has ever set, so both non-identifying states clear it
 * (Astra H5-N01). Left set across an anonymous interval, it made the sequence
 * HMAC A, then a missing analyticsId while still signed in, then the SAME HMAC A
 * go anonymous for the rest of the mount: the equality check below matched its
 * own stale record and returned, after `enterAnonymousMode` had already reset the
 * browser. Nothing forbidden was ever emitted there, so the defect was
 * attribution, not privacy. Clearing the record has two consequences worth
 * naming. A restored id is identified again, which is a real second `$identify`
 * merge from a new anonymous id, so `signup_completed` fires again with it,
 * exactly as it already did across a logout and a login in one mount. And a
 * logout that FOLLOWS such an interval no longer calls `resetPostHog`: the
 * identity that reset exists to clear was already left behind one state earlier
 * by `reset(true)`, which is the stronger of the two, and `enterAnonymousMode`
 * still runs on the logout path. A logout straight from an identified session is
 * unchanged.
 *
 * The logout branch still tests the SESSION, not the id, so an unprovisioned
 * key never looks like a logout. The request reaches the library even while the
 * SDK is loading or consent is absent; the library only records it and applies
 * it inside posthog-js's `loaded` hook, before anything is captured.
 * `signup_completed` still waits for a live client, as the whole branch used to.
 */
function AuthIdentifier() {
  const { data: session, status } = useSession();
  const lastIdentified = useRef<string | null>(null);

  useEffect(() => {
    if (status === "loading") return;

    const analyticsId = session?.user?.analyticsId ?? null;
    const signedIn = Boolean(session?.user);

    if (analyticsId) {
      if (analyticsId === lastIdentified.current) return;
      const user = session?.user as
        | { plan?: string; compassTier?: string }
        | undefined;
      identifyUser(analyticsId, {
        tier_reports: user?.plan ?? "free",
        tier_compass: user?.compassTier ?? "none",
      });
      // Not live yet (SDK still loading, or no consent): the library has
      // recorded the identity and applies it before the first capture. The
      // event and the ref wait for a later run, exactly as before.
      if (!posthogClient()) return;
      // `signup_completed` fires on every first-identification of a
      // session (post-OAuth callback). The funnel-analytics distinction
      // between "fresh signup" and "returning login" lives on the user's
      // created_at field in the auth DB, which analytics queries can join
      // PostHog's distinct_id back to for a precise breakdown. It stays
      // bound to identification, so an unprovisioned key loses this event
      // rather than firing it detached from any person.
      trackEvent("signup_completed", {
        tier_reports: user?.plan ?? "free",
        tier_compass: user?.compassTier ?? "none",
      });
      lastIdentified.current = analyticsId;
      return;
    }

    if (!signedIn && lastIdentified.current) {
      resetPostHog();
    }
    // No analyticsId: signed in with the key unprovisioned, or signed out.
    // Either way this component is giving up whatever identity it had in force,
    // so the record of it goes too (H5-N01); otherwise the same id returning
    // later reads as a no-op and the session stays anonymous. Cleared AFTER the
    // logout test above, which asks the same question this records the answer to.
    lastIdentified.current = null;
    // Idempotent in the library, so running on every session change is fine.
    enterAnonymousMode();
  }, [session, status]);

  return null;
}

// Re-export helper used downstream by event-firing code paths.
export { setUserProperties };
